import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Fuel, Truck, Receipt, Plus, Trash2, Clock, CheckCircle2, AlertTriangle, Eye } from 'lucide-react';
import { openReceipt } from './FuelReceiptUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { value: 'fuel', label: 'Fuel Expense', Icon: Fuel },
  { value: 'truck_lease', label: 'Truck Lease Fee', Icon: Truck },
  { value: 'other', label: 'Other Deduction', Icon: Receipt },
];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString() : '');
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ category: 'fuel', amount: '', txn_date: today(), description: '', notes: '', truck_unit: '', gallons: '', receipt_ref: '' });

const details = (d) => [fmtDate(d.txn_date), d.category === 'other' ? d.description : null, d.truck_unit ? `Unit ${d.truck_unit}` : null,
  d.gallons ? `${d.gallons} gal` : null, d.receipt_ref ? `Receipt ${d.receipt_ref}` : null, d.notes].filter(Boolean).join(' · ');

const Row = ({ d, onDelete }) => {
  const cat = CATEGORIES.find((c) => c.value === d.category) || CATEGORIES[2];
  const needsRevision = d.status === 'pending' && d.finalized_run_for_period;
  return (
    <tr data-testid={`drv-deduction-row-${d.id}`} className="border-b border-slate-100 last:border-0">
      <td className="py-2 pr-3"><div className="flex items-center gap-2"><cat.Icon className="w-4 h-4 text-slate-500" /><span className="text-sm font-medium text-slate-900">{cat.label}</span></div>
        <p className="text-xs text-slate-500 pl-6">{details(d)}</p></td>
      <td className="py-2 pr-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`drv-deduction-period-${d.id}`}>{d.pay_period?.label || '—'}</td>
      <td className="py-2 pr-3 text-sm font-semibold text-slate-900 text-right whitespace-nowrap">-{money(d.amount)}</td>
      <td className="py-2 pr-3 whitespace-nowrap">
        {d.status === 'applied' ? (
          <span data-testid={`drv-deduction-status-${d.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"><CheckCircle2 className="w-3 h-3" />Applied{d.applied_payroll?.revision > 1 ? ` (rev ${d.applied_payroll.revision})` : ''}</span>
        ) : needsRevision ? (
          <span data-testid={`drv-deduction-status-${d.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" title="A finalized payroll already exists for this period — apply a revision on that payroll run"><AlertTriangle className="w-3 h-3" />Needs payroll revision</span>
        ) : (
          <span data-testid={`drv-deduction-status-${d.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700"><Clock className="w-3 h-3" />Pending — next payroll</span>
        )}
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        {d.expense_id && (
          <button data-testid={`drv-view-receipt-${d.id}`} onClick={() => openReceipt(d.expense_id)} className="text-[#002FA7] hover:text-[#00247D] mr-2" title="Open original fuel receipt"><Eye className="w-4 h-4" /></button>
        )}
        {d.status === 'pending' && !d.expense_id && (
          <button data-testid={`drv-delete-deduction-${d.id}`} onClick={() => onDelete(d)} className="text-red-600 hover:text-red-800" title="Remove pending deduction"><Trash2 className="w-4 h-4" /></button>
        )}
      </td>
    </tr>
  );
};

export const DriverPeriodDeductions = ({ driverId, driverName, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/period-deductions`, { params: { driver_id: driverId }, withCredentials: true });
      setRows(res.data);
    } catch (_e) { /* non-admins never render this section */ }
  }, [driverId]);
  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  const save = async (allowDuplicate = false) => {
    setSaving(true);
    try {
      const body = { driver_id: driverId, category: form.category, amount: parseFloat(form.amount), txn_date: form.txn_date, allow_duplicate: allowDuplicate,
        description: form.description || null, notes: form.notes || null, truck_unit: form.truck_unit || null,
        gallons: form.gallons ? parseFloat(form.gallons) : null, receipt_ref: form.receipt_ref || null };
      const res = await axios.post(`${API}/period-deductions`, body, { withCredentials: true });
      const pp = res.data.pay_period;
      toast.success(`${res.data.category_label} saved — assigned to pay period ${pp ? pp.label : ''}`);
      setForm(emptyForm());
      setOpen(false);
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'possible_duplicate') {
        if (window.confirm(`${detail.message}\n\nSave this as a separate transaction?`)) return save(true);
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to save deduction');
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove this pending ${d.category_label} of ${money(d.amount)} dated ${fmtDate(d.txn_date)}?`)) return;
    try {
      await axios.delete(`${API}/period-deductions/${d.id}`, { withCredentials: true });
      toast.success('Pending deduction removed');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove deduction');
    }
  };

  const pending = rows.filter((r) => r.status === 'pending');
  const applied = rows.filter((r) => r.status === 'applied');
  const sum = (list) => list.reduce((a, r) => a + Number(r.amount || 0), 0);
  const byCat = (list) => CATEGORIES.map((c) => ({ ...c, total: sum(list.filter((r) => r.category === c.value)) })).filter((c) => c.total > 0);

  return (
    <div data-testid="driver-period-deductions" className="mb-8">
      <div className="flex items-center justify-between mb-3 pt-4 border-t border-slate-200">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Payroll Deductions &amp; Expenses</h2>
          <p className="text-xs text-slate-500 mt-1">Truck lease fees, fuel purchases and other deductions for {driverName}. Each transaction is assigned to the Sunday–Saturday pay period of its date and deducted once when that payroll is generated. Entries dated inside an already-finalized week require an explicit payroll revision.</p>
        </div>
        <button data-testid="drv-add-deduction-button" onClick={() => setOpen(!open)} className="shrink-0 inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700">
          <Plus className="w-4 h-4" /> Add deduction
        </button>
      </div>
      {open && (
        <form data-testid="drv-deduction-form" onSubmit={(e) => { e.preventDefault(); save(false); }} className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 p-4 bg-slate-50 border border-slate-200 rounded-md">
          <select data-testid="drv-pd-category" value={form.category} onChange={set('category')} className="border border-slate-300 rounded-md px-2 py-2 text-sm bg-white">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <input data-testid="drv-pd-amount" type="number" step="0.01" min="0.01" required placeholder="Amount" value={form.amount} onChange={set('amount')} className="border border-slate-300 rounded-md px-2 py-2 text-sm" />
          <div>
            <input data-testid="drv-pd-date" type="date" required value={form.txn_date} onChange={set('txn_date')} className="w-full border border-slate-300 rounded-md px-2 py-2 text-sm" />
            <p className="text-[11px] text-slate-500 mt-0.5">{form.category === 'fuel' ? 'Actual fueling date' : form.category === 'truck_lease' ? 'Lease period date' : 'Deduction date'}</p>
          </div>
          {form.category === 'other' && <input data-testid="drv-pd-description" required placeholder="Description" value={form.description} onChange={set('description')} className="border border-slate-300 rounded-md px-2 py-2 text-sm" />}
          {form.category === 'fuel' && (<>
            <input data-testid="drv-pd-truck-unit" placeholder="Truck / unit #" value={form.truck_unit} onChange={set('truck_unit')} className="border border-slate-300 rounded-md px-2 py-2 text-sm" />
            <input data-testid="drv-pd-gallons" type="number" step="0.01" min="0" placeholder="Gallons" value={form.gallons} onChange={set('gallons')} className="border border-slate-300 rounded-md px-2 py-2 text-sm" />
            <input data-testid="drv-pd-receipt" placeholder="Receipt # / reference" value={form.receipt_ref} onChange={set('receipt_ref')} className="border border-slate-300 rounded-md px-2 py-2 text-sm" />
          </>)}
          <input data-testid="drv-pd-notes" placeholder="Notes (optional)" value={form.notes} onChange={set('notes')} className="border border-slate-300 rounded-md px-2 py-2 text-sm col-span-2" />
          <div className="col-span-2 md:col-span-4 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 text-sm rounded-md border border-slate-300">Cancel</button>
            <button data-testid="drv-pd-save" type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold rounded-md bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50">{saving ? 'Saving…' : 'Save deduction'}</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div data-testid="drv-pending-summary" className="rounded-md border border-slate-200 p-3 bg-white">
          <p className="text-[11px] font-semibold text-slate-500 uppercase">Pending (not yet in payroll)</p>
          <p className="text-lg font-bold text-slate-900">{money(sum(pending))}</p>
          <p className="text-xs text-slate-500">{pending.length} transaction{pending.length === 1 ? '' : 's'}{byCat(pending).length ? ' · ' + byCat(pending).map((c) => `${c.label} ${money(c.total)}`).join(' · ') : ''}</p>
        </div>
        <div data-testid="drv-applied-summary" className="rounded-md border border-slate-200 p-3 bg-white">
          <p className="text-[11px] font-semibold text-slate-500 uppercase">Applied to payroll</p>
          <p className="text-lg font-bold text-slate-900">{money(sum(applied))}</p>
          <p className="text-xs text-slate-500">{applied.length} transaction{applied.length === 1 ? '' : 's'} across {new Set(applied.map((r) => r.applied_payroll_id)).size} payroll run{new Set(applied.map((r) => r.applied_payroll_id)).size === 1 ? '' : 's'}</p>
        </div>
        <div className="rounded-md border border-slate-200 p-3 bg-white">
          <p className="text-[11px] font-semibold text-slate-500 uppercase">Safeguards</p>
          <p className="text-xs text-slate-600 mt-1">Each transaction is applied once; applied rows are locked; identical same-day entries ask for confirmation; finalized weeks need an explicit revision.</p>
        </div>
      </div>

      <table className="w-full text-left">
        <thead><tr className="text-[11px] uppercase text-slate-500 border-b border-slate-200"><th className="py-1.5 pr-3">Transaction</th><th className="py-1.5 pr-3">Pay period</th><th className="py-1.5 pr-3 text-right">Amount</th><th className="py-1.5 pr-3">Status</th><th /></tr></thead>
        <tbody>
          {pending.length === 0 && applied.length === 0 && <tr><td colSpan={5} className="py-4 text-sm text-slate-500 text-center" data-testid="drv-deductions-empty">No truck lease, fuel or other deductions recorded yet.</td></tr>}
          {pending.map((d) => <Row key={d.id} d={d} onDelete={remove} />)}
          {(showHistory ? applied : applied.slice(0, 5)).map((d) => <Row key={d.id} d={d} />)}
        </tbody>
      </table>
      {applied.length > 5 && (
        <button data-testid="drv-toggle-history" onClick={() => setShowHistory(!showHistory)} className="mt-2 text-xs font-semibold text-blue-700 hover:underline">
          {showHistory ? 'Show recent only' : `Show full history (${applied.length} applied)`}
        </button>
      )}
    </div>
  );
};
